import React, { useState, useCallback } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import { BACKEND_URL } from '../config';
import { fetchWithOrg } from '../utils/api';
import { useOrganization } from '../context/OrganizationContext';
import { useUserRole } from '../hooks/useUserRole';

import {
  Calendar,
  CheckCircle,
  CircleNotch,
  CurrencyDollar,
  DownloadSimple,
  FileArrowDown,
  FileText,
  Funnel,
  Robot,
  WarningCircle
} from '@phosphor-icons/react';
const AnalyticsReports = () => {
  const { activeOrganization } = useOrganization();
  const { role, isAdmin } = useUserRole();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  
  // Funnel states
  const [dateRange, setDateRange] = useState(30);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [agents, setAgents] = useState([]);
  
  // Load agents for filtering
  React.useEffect(() => {
    const loadAgents = async () => {
try {
  const response = await fetchWithOrg(
    `${BACKEND_URL}/api/agents`,
    {},
    activeOrganization?.organization_id
  );
  if (response.ok) {
    const data = await response.json();
    setAgents(data.agents || []);
  }
} catch (err) {
  console.error('Error loading agents:', err);
}
    };
    
    if (activeOrganization) {
loadAgents();
    }
  }, [activeOrganization]);

  const getDateRangeParams = () => {
    if (useCustomRange && customStartDate && customEndDate) {
const start = new Date(customStartDate);
const end = new Date(customEndDate);
const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
return { days: Math.max(1, Math.min(days, 90)) };
    }
    return { days: dateRange };
  };

  const handleExportCSV = async () => {
    if (!isAdmin) {
setError('CSV export is only available to administrators');
setTimeout(() => setError(null), 5000);
return;
    }

    try {
setLoading(true);
setError(null);

const params = getDateRangeParams();
const queryParams = new URLSearchParams({
  days: params.days.toString(),
  ...(selectedAgent !== 'all' && { agent_id: selectedAgent })
});

const response = await fetchWithOrg(
  `${BACKEND_URL}/api/analytics/export/usage-logs?${queryParams}`,
  {},
  activeOrganization.organization_id
);

if (!response.ok) {
  throw new Error('Failed to export data');
}

const data = await response.json();

// Convert to CSV
if (data.data && data.data.length > 0) {
  const csvContent = convertToCSV(data.data);
  downloadCSV(csvContent, `analytics-export-${Date.now()}.csv`);
  setSuccess(`Successfully exported ${data.count} records`);
  setTimeout(() => setSuccess(null), 5000);
} else {
  setError('No data available for export');
  setTimeout(() => setError(null), 5000);
}

    } catch (err) {
console.error('Error exporting CSV:', err);
setError(err.message || 'Failed to export data');
setTimeout(() => setError(null), 5000);
    } finally {
setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    if (!isAdmin) {
setError('PDF export is only available to administrators');
setTimeout(() => setError(null), 5000);
return;
    }

    try {
setLoading(true);
setError(null);

const params = getDateRangeParams();

// Fetch summary data for PDF
const dashboardRes = await fetchWithOrg(
  `${BACKEND_URL}/api/analytics/dashboard?days=${params.days}`,
  {},
  activeOrganization.organization_id
);

const costBreakdownRes = await fetchWithOrg(
  `${BACKEND_URL}/api/analytics/costs/breakdown?days=${params.days}`,
  {},
  activeOrganization.organization_id
);

if (!dashboardRes.ok || !costBreakdownRes.ok) {
  throw new Error('Failed to fetch report data');
}

const dashboardData = await dashboardRes.json();
const costData = await costBreakdownRes.json();

// Generate PDF
generatePDF(dashboardData, costData, params.days);

setSuccess('PDF report generated successfully');
setTimeout(() => setSuccess(null), 5000);

    } catch (err) {
console.error('Error generating PDF:', err);
setError(err.message || 'Failed to generate PDF report');
setTimeout(() => setError(null), 5000);
    } finally {
setLoading(false);
    }
  };

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvRows = [
headers.join(','),
...data.map(row =>
  headers.map(header => {
    const value = row[header];
    // Escape commas and quotes
    if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }).join(',')
)
    ];
    
    return csvRows.join('\n');
  };

  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const generatePDF = (dashboardData, costData, days) => {
    // Create a simple HTML report that can be printed to PDF
    const reportWindow = window.open('', '_blank');
    
    const html = `
<!DOCTYPE html>
<html>
<head>
  <title>Analytics Report - ${new Date().toLocaleDateString()}</title>
  <style>
    body {
font-family: Arial, sans-serif;
padding: 40px;
max-width: 900px;
margin: 0 auto;
color: #1a202c;
    }
    .header {
text-align: center;
margin-bottom: 40px;
padding-bottom: 20px;
border-bottom: 3px solid #6366F1;
    }
    .logo {
font-size: 28px;
font-weight: bold;
color: #6366F1;
margin-bottom: 10px;
    }
    h1 {
color: #1a202c;
font-size: 32px;
margin: 10px 0;
    }
    .date-range {
color: #718096;
font-size: 14px;
    }
    .section {
margin: 30px 0;
    }
    .section-title {
font-size: 20px;
font-weight: bold;
color: #2d3748;
margin-bottom: 15px;
padding-bottom: 10px;
border-bottom: 2px solid #e2e8f0;
    }
    .kpi-grid {
display: grid;
grid-template-columns: repeat(2, 1fr);
gap: 20px;
margin: 20px 0;
    }
    .kpi-card {
padding: 20px;
background: #f7fafc;
border-radius: 8px;
border: 1px solid #e2e8f0;
    }
    .kpi-label {
font-size: 12px;
color: #718096;
text-transform: uppercase;
letter-spacing: 0.5px;
    }
    .kpi-value {
font-size: 28px;
font-weight: bold;
color: #1a202c;
margin-top: 8px;
    }
    .table {
width: 100%;
border-collapse: collapse;
margin: 20px 0;
    }
    .table th {
background: #f7fafc;
padding: 12px;
text-align: left;
font-weight: 600;
color: #2d3748;
border-bottom: 2px solid #e2e8f0;
    }
    .table td {
padding: 12px;
border-bottom: 1px solid #e2e8f0;
    }
    .footer {
margin-top: 60px;
padding-top: 20px;
border-top: 1px solid #e2e8f0;
text-align: center;
color: #718096;
font-size: 12px;
    }
    @media print {
body { padding: 20px; }
.no-print { display: none; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="logo">Man & Machine</div>
    <h1>Analytics Report</h1>
    <div class="date-range">
Period: Last ${days} days | Generated: ${new Date().toLocaleString()}
    </div>
  </div>

  <div class="section">
    <div class="section-title">Key Performance Indicators</div>
    <div class="kpi-grid">
<div class="kpi-card">
  <div class="kpi-label">Total Cost</div>
  <div class="kpi-value">$${(dashboardData.total_cost_usd || 0).toFixed(2)}</div>
</div>
<div class="kpi-card">
  <div class="kpi-label">API Calls</div>
  <div class="kpi-value">${(dashboardData.total_calls || 0).toLocaleString()}</div>
</div>
<div class="kpi-card">
  <div class="kpi-label">Success Rate</div>
  <div class="kpi-value">${(dashboardData.success_rate || 0)}%</div>
</div>
<div class="kpi-card">
  <div class="kpi-label">Avg Response Time</div>
  <div class="kpi-value">${(dashboardData.avg_response_time_ms || 0)} ms</div>
</div>
    </div>
  </div>

  ${dashboardData.top_agents && dashboardData.top_agents.length > 0 ? `
  <div class="section">
    <div class="section-title">Top Agents by Usage</div>
    <table class="table">
<thead>
  <tr>
    <th>Agent</th>
    <th>Calls</th>
    <th>Cost</th>
    <th>Success Rate</th>
  </tr>
</thead>
<tbody>
  ${dashboardData.top_agents.map(agent => `
    <tr>
<td>${agent.agent_name || 'Unknown'}</td>
<td>${agent.calls.toLocaleString()}</td>
<td>$${agent.cost.toFixed(2)}</td>
<td>${agent.success_rate}%</td>
    </tr>
  `).join('')}
</tbody>
    </table>
  </div>
  ` : ''}

  ${costData.model_breakdown && costData.model_breakdown.length > 0 ? `
  <div class="section">
    <div class="section-title">Cost Breakdown by Model</div>
    <table class="table">
<thead>
  <tr>
    <th>Model</th>
    <th>Provider</th>
    <th>Calls</th>
    <th>Cost</th>
    <th>Percentage</th>
  </tr>
</thead>
<tbody>
  ${costData.model_breakdown.map(model => `
    <tr>
<td>${model.model}</td>
<td>${model.provider || 'N/A'}</td>
<td>${model.calls.toLocaleString()}</td>
<td>$${model.cost_usd.toFixed(2)}</td>
<td>${model.percentage}%</td>
    </tr>
  `).join('')}
</tbody>
    </table>
  </div>
  ` : ''}

  <div class="footer">
    <p>This report was generated automatically by the Man & Machine Analytics Platform</p>
    <p>For questions or support, please contact your account administrator</p>
  </div>

  <div class="no-print" style="margin-top: 40px; text-align: center;">
    <button onclick="window.print()" style="
padding: 12px 24px;
background: linear-gradient(to right, #6366F1, #EC4899);
color: white;
border: none;
border-radius: 8px;
font-size: 16px;
font-weight: 600;
cursor: pointer;
    ">Print / Save as PDF</button>
  </div>
</body>
</html>
    `;
    
    reportWindow.document.write(html);
    reportWindow.document.close();
  };

  // Access control check
  if (!isAdmin && role) {
    return (
<div className="min-h-screen bg-[#161B26]">
  <Header />
  <div className="pt-32 pb-20">
    <div className="max-w-4xl mx-auto px-6">
<div className="glass rounded-2xl p-12 text-center">
  <WarningCircle size={64} className="mx-auto mb-6 text-[#F59E0B]" />
  <h2 className="text-2xl font-bold text-white mb-4">Admin Access Required</h2>
  <p className="text-[#A0AEC0] mb-8">
    Report generation and data export features are only available to organization administrators.
  </p>
  <button
    onClick={() => navigate('/analytics')}
    className="px-6 py-3 rounded-full bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white font-semibold hover:opacity-90 transition-opacity"
  >
    Back to Analytics
  </button>
</div>
    </div>
  </div>
  
</div>
    );
  }

  return (
      <>
<div className="max-w-5xl mx-auto">
    {/* Header */}
    <div className="mb-8">
<div className="flex items-center gap-3 mb-2">
  <FileText size={32} className="text-[#6366F1]" />
  <h1 className="text-4xl sm:text-5xl font-bold text-white">Analytics Reports</h1>
</div>
<p className="text-[#A0AEC0] text-base">
  Export usage data and generate comprehensive analytics reports
</p>
    </div>

    {/* Success/Error Messages */}
    <AnimatePresence>
{success && (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0 }}
    className="mb-6 p-4 rounded-xl bg-[rgba(20,241,149,0.1)] border border-[rgba(20,241,149,0.3)] flex items-center gap-3"
  >
    <CheckCircle size={20} className="text-[#14F195]" />
    <p className="text-[#14F195] text-sm">{success}</p>
  </motion.div>
)}
{error && (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0 }}
    className="mb-6 p-4 rounded-xl bg-[rgba(239,68,68,0.1)] border border-[rgba(239,68,68,0.3)] flex items-center gap-3"
  >
    <WarningCircle size={20} className="text-[#EF4444]" />
    <p className="text-[#EF4444] text-sm">{error}</p>
  </motion.div>
)}
    </AnimatePresence>

    {/* Filters */}
    <div className="glass rounded-2xl p-6 mb-8">
<h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
  <Funnel size={20} className="text-[#6366F1]" />
  Report Filters
</h2>

<div className="space-y-4">
  {/* Date Range Toggle */}
  <div className="flex items-center gap-3">
    <button
onClick={() => setUseCustomRange(false)}
className={`px-4 py-2 rounded-full font-semibold transition-all ${
  !useCustomRange
    ? 'bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white'
    : 'glass text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)]'
}`}
    >
Quick Range
    </button>
    <button
onClick={() => setUseCustomRange(true)}
className={`px-4 py-2 rounded-full font-semibold transition-all ${
  useCustomRange
    ? 'bg-gradient-to-r from-[#6366F1] to-[#EC4899] text-white'
    : 'glass text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)]'
}`}
    >
Custom Range
    </button>
  </div>

  {/* Quick Date Range */}
  {!useCustomRange && (
    <div className="flex gap-2 flex-wrap">
{[7, 30, 90].map((days) => (
  <button
    key={days}
    onClick={() => setDateRange(days)}
    className={`px-4 py-2 rounded-full font-semibold transition-all ${
dateRange === days
  ? 'bg-[#6366F1] text-white'
  : 'glass text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)]'
    }`}
  >
    Last {days} days
  </button>
))}
    </div>
  )}

  {/* Custom Date Range */}
  {useCustomRange && (
    <div className="grid md:grid-cols-2 gap-4">
<div>
  <label className="block text-[#A0AEC0] text-sm mb-2">Start Date</label>
  <input
    type="date"
    value={customStartDate}
    onChange={(e) => setCustomStartDate(e.target.value)}
    className="w-full px-4 py-2 rounded-xl glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
  />
</div>
<div>
  <label className="block text-[#A0AEC0] text-sm mb-2">End Date</label>
  <input
    type="date"
    value={customEndDate}
    onChange={(e) => setCustomEndDate(e.target.value)}
    className="w-full px-4 py-2 rounded-xl glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
  />
</div>
    </div>
  )}

  {/* Agent Funnel */}
  <div>
    <label className="block text-[#A0AEC0] text-sm mb-2">Funnel by Agent</label>
    <select
value={selectedAgent}
onChange={(e) => setSelectedAgent(e.target.value)}
className="w-full px-4 py-2 rounded-xl glass text-white focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
    >
<option value="all">All Agents</option>
{agents.map(agent => (
  <option key={agent.agent_id} value={agent.agent_id}>
    {agent.name}
  </option>
))}
    </select>
  </div>
</div>
    </div>

    {/* Export Options */}
    <div className="grid md:grid-cols-2 gap-6">
{/* CSV Export */}
<div className="glass rounded-2xl p-6">
  <div className="flex items-center gap-3 mb-4">
    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] flex items-center justify-center">
<FileArrowDown size={24} className="text-white" />
    </div>
    <div>
<h3 className="text-xl font-bold text-white">CSV Export</h3>
<p className="text-[#A0AEC0] text-sm">Raw usage data</p>
    </div>
  </div>
  
  <p className="text-[#A0AEC0] text-sm mb-6">
    Export detailed usage logs including timestamps, models used, costs, and performance metrics.
    Perfect for custom analysis in spreadsheet tools.
  </p>

  <button
    onClick={handleExportCSV}
    disabled={loading}
    className="w-full px-6 py-3 rounded-full bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
  >
    {loading ? (
<>
  <CircleNotch size={20} className="animate-spin" />
  Exporting...
</>
    ) : (
<>
  <DownloadSimple size={20} />
  Export CSV
</>
    )}
  </button>
</div>

{/* PDF Report */}
<div className="glass rounded-2xl p-6">
  <div className="flex items-center gap-3 mb-4">
    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#EC4899] to-[#F59E0B] flex items-center justify-center">
<FileText size={24} className="text-white" />
    </div>
    <div>
<h3 className="text-xl font-bold text-white">PDF Report</h3>
<p className="text-[#A0AEC0] text-sm">Executive summary</p>
    </div>
  </div>
  
  <p className="text-[#A0AEC0] text-sm mb-6">
    Generate a professionally formatted PDF report with KPIs, charts, and summaries.
    Ideal for sharing with stakeholders and management.
  </p>

  <button
    onClick={handleExportPDF}
    disabled={loading}
    className="w-full px-6 py-3 rounded-full bg-gradient-to-r from-[#EC4899] to-[#F59E0B] text-white font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
  >
    {loading ? (
<>
  <CircleNotch size={20} className="animate-spin" />
  Generating...
</>
    ) : (
<>
  <FileText size={20} />
  Generate PDF
</>
    )}
  </button>
</div>
    </div>

    {/* Info Box */}
    <div className="mt-8 glass rounded-2xl p-6 border border-[rgba(99,102,241,0.3)]">
<h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
  <Calendar size={20} className="text-[#6366F1]" />
  Data Retention & Limits
</h3>
<ul className="space-y-2 text-[#A0AEC0] text-sm">
  <li className="flex items-start gap-2">
    <CheckCircle size={16} className="text-[#14F195] mt-0.5 flex-shrink-0" />
    <span>Usage data is retained for 90 days</span>
  </li>
  <li className="flex items-start gap-2">
    <CheckCircle size={16} className="text-[#14F195] mt-0.5 flex-shrink-0" />
    <span>CSV exports limited to 10,000 most recent records</span>
  </li>
  <li className="flex items-start gap-2">
    <CheckCircle size={16} className="text-[#14F195] mt-0.5 flex-shrink-0" />
    <span>PDF reports include summary statistics and top performers</span>
  </li>
  <li className="flex items-start gap-2">
    <CheckCircle size={16} className="text-[#14F195] mt-0.5 flex-shrink-0" />
    <span>Report generation respects your current organization and role permissions</span>
  </li>
</ul>
    </div>
  </div>
    </>
  );
};

export default AnalyticsReports;
